

export enum ViewState {
  DASHBOARD = 'DASHBOARD',
  MARKETPLACE = 'MARKETPLACE', // Project finding KOLs
  PROJECT_MARKETPLACE = 'PROJECT_MARKETPLACE', // KOL finding Projects
  SQUADS = 'SQUADS', // New: KOL Bundles
  POST_CAMPAIGN = 'POST_CAMPAIGN', // New: Project posting requirements
  MY_CAMPAIGNS = 'MY_CAMPAIGNS', // New: Project managing their campaigns
  KOL_PROFILE = 'KOL_PROFILE',
  CREATE_BRIEF = 'CREATE_BRIEF',
  ORDERS = 'ORDERS',
  ORDER_DETAIL = 'ORDER_DETAIL', // New view for monitoring
  ANALYTICS = 'ANALYTICS',
  SETTINGS = 'SETTINGS',
  CHAT = 'CHAT', // New: Messaging System
  ADMIN_DASHBOARD = 'ADMIN_DASHBOARD', // New: Platform Owner View
  DISPUTES = 'DISPUTES', // New: Justice System
  REFERRALS = 'REFERRALS' // New: Growth Engine
}

export enum UserRole {
  PROJECT = 'PROJECT',
  KOL = 'KOL',
  ADMIN = 'ADMIN' // New Role
}

export enum KolCategory {
  ALL = 'All',
  DEFI = 'DeFi',
  NFT = 'NFT',
  GAMEFI = 'GameFi',
  L1_L2 = 'L1/L2',
  MEME = 'Meme',
  TRADING = 'Trading',
  LIFESTYLE = 'Lifestyle',
  TECH = 'Tech'
}

export enum SocialPlatform {
  TWITTER = 'Twitter',
  YOUTUBE = 'YouTube',
  TIKTOK = 'TikTok',
  INSTAGRAM = 'Instagram',
  FACEBOOK = 'Facebook',
  BILIBILI = 'Bilibili',
  RED_BOOK = 'XiaoHongShu' // Little Red Book
}

export interface SocialAccount {
  platform: SocialPlatform;
  handle: string;
  url: string;
  followers: number;
  verified: boolean; // New: Verification status
}

export interface AiAnalysis {
  overallScore: number; // 0-100
  authenticity: number; // 0-100 (Real vs Bot)
  sentiment: number; // 0-100 (Positive engagement)
  commercialValue: number; // 0-100 (Conversion potential)
  brandSafety: number; // 0-100 (Risk of FUD/Scam)
  consistency: number; // 0-100 (Posting regularity)
  summary: string;
  strengths: string[];
  weaknesses: string[];
}

export interface ServicePackage {
  id: string;
  title: string;
  description: string;
  platform: SocialPlatform;
  price: number;
  deliveryDays: number;
  features: string[];
}

export interface Review {
  id: string;
  reviewerName: string; // Project Name
  reviewerAvatar?: string;
  rating: number; // 1-5
  comment: string;
  date: string;
  orderId: string;
}

// --- New: SBT Badge System ---
export interface SbtBadge {
  id: string;
  name: string;
  description: string;
  icon: string; // Lucide icon name or image URL
  dateEarned: string;
  rarity: 'Common' | 'Rare' | 'Epic' | 'Legendary';
  txHash?: string; // On-chain proof
}

export interface KolProfile {
  id: string;
  name: string;
  handle: string; // Primary handle (usually Twitter)
  avatar: string;
  categories: KolCategory[];
  tags: string[]; // New field for specific AI tags
  totalFollowers: number; // Aggregated
  socialAccounts: SocialAccount[]; // New field for multi-platform
  engagementScore: number; // 0-100
  avgViews: number;
  priceRange: { min: number; max: number; currency: string };
  trustScore: number; // 0-100 based on previous successful contracts
  bio: string;
  languages: string[];
  recentCampaigns: { project: string; roi: number }[];
  reviews: Review[]; // New field for reviews
  aiData: AiAnalysis;
  packages: ServicePackage[]; // New field for templates
  sbtBadges?: SbtBadge[]; // New: Soulbound Tokens
  points: number; // New: Loyalty Points
}

// --- New: KOL Squads Application ---
export interface SquadApplication {
  id: string;
  kolId: string;
  kolName: string;
  kolAvatar: string;
  kolHandle: string;
  reason: string;
  proposedRate: number; // New: The price they want to contribute to the squad
  date: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
}

export interface SquadMember {
  kolId: string;
  rate: number; // Their portion of the total value
}

// --- New: KOL Squads ---
export interface KolSquad {
  id: string;
  name: string;
  description: string;
  coverImage: string;
  leaderId: string; // The KOL who manages the squad
  members: SquadMember[]; // Updated: Object array to track rates
  totalFollowers: number;
  totalPrice: number; // Calculated: Sum(rates) * (1-discount)
  discountPercentage: number;
  tags: string[];
  applications?: SquadApplication[]; // New: Manage join requests
}

export interface CampaignApplication {
  id: string;
  kolId: string;
  kolName: string;
  kolAvatar: string;
  kolHandle: string;
  pitch: string;
  proposedPrice: number;
  date: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
}

// --- New: Hybrid Payment Types ---
export enum PaymentType {
  USDT = 'USDT',
  HYBRID = 'HYBRID' // USDT + Tokens
}

export interface TokenDetails {
  symbol: string;
  amount: number;
  contractAddress?: string;
  vestingTerms: string; // e.g., "50% TGE, 50% Linear 6 months"
}

export interface ProjectProfile {
  id: string;
  name: string;
  logo: string;
  industry: KolCategory;
  description: string;
  website: string;
  budgetRange: string; // e.g., "$1k - $5k"
  tags: string[];
  activeCampaigns: number;
  verified: boolean;
  applications?: CampaignApplication[]; // New: Track who applied
  escrowed?: boolean; // New: Is budget locked?
  paymentType?: PaymentType; // New
  tokenDetails?: TokenDetails; // New
  points: number; // New: Loyalty Points
}

export enum OrderStatus {
  PENDING_ACCEPTANCE = 'Pending Acceptance', // New: Project staked, waiting for KOL
  NEGOTIATING = 'Negotiating',
  ESCROW_PENDING = 'Escrow Pending',
  IN_PROGRESS = 'In Progress',
  SUBMITTED = 'Submitted', // New: KOL has submitted work, 48h timer starts
  REVIEW_DATA = 'Reviewing Data',
  COMPLETED = 'Completed',
  DISPUTED = 'Disputed'
}

// --- New: AI Dispute Analysis ---
export interface DisputeAiAnalysis {
  recommendation: 'REFUND' | 'RELEASE' | 'UNCERTAIN';
  confidenceScore: number; // 0-100
  reasoning: string;
}

// --- New Types for Chat ---
export interface ChatMessage {
  id: string;
  senderId: string;
  text: string;
  timestamp: string;
  isMe: boolean;
}

export interface ChatConversation {
  id: string;
  partnerId: string; // The ID of the person you are chatting with
  partnerName: string;
  partnerAvatar: string;
  partnerRole: UserRole;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  messages: ChatMessage[];
}

// --- New: Dispute System ---
export interface Dispute {
  id: string;
  orderId: string;
  initiatorName: string; // Who started dispute
  respondentName: string;
  reason: string;
  description: string;
  status: 'OPEN' | 'RESOLVED_REFUND' | 'RESOLVED_RELEASE';
  evidence: string[]; // URLs or text
  votesForInitiator: number;
  votesForRespondent: number;
  dateCreated: string;
  amountLocked: number;
  aiAnalysis?: DisputeAiAnalysis; // New: AI Judge
  chatTranscript?: ChatMessage[]; // New: Historical context for admin
  adminReason?: string; // New: Admin's written verdict reason
  resolvedDate?: string; // New: Date resolved
}

export interface AiMonitoringData {
  currentViews: number;
  targetViews: number;
  currentLikes: number;
  timeElapsedPercent: number; // 0-100
  status: 'on_track' | 'at_risk' | 'critical';
  aiInsight: string; // "Velocity is good, estimated +20% over-delivery"
  hourlyData: { hour: string; views: number; target: number }[];
}

export interface Order {
  id: string;
  kolId: string;
  kolName: string;
  kolAvatar: string;
  projectName: string;
  amount: number;
  currency: string;
  status: OrderStatus;
  dateCreated: string;
  dueDate: string;
  serviceTitle?: string; // Which package was bought
  monitoring?: AiMonitoringData; // Real-time AI data
  review?: Review; // The review left for this order
  
  // New Hybrid Payment Fields in Order
  paymentType?: PaymentType;
  tokenDetails?: TokenDetails;

  submissionTime?: string; // New: ISO String when status became SUBMITTED
}

export interface ChartDataPoint {
  name: string;
  value: number;
  value2?: number;
}

// --- New Types for Notifications ---
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  date: string;
  read: boolean;
  linkTo?: ViewState;
}

// Supported 18 Languages
export type Language = 
  | 'en' // English
  | 'zh' // Chinese (Simplified)
  | 'zh-TW' // Traditional Chinese
  | 'ko' // Korean
  | 'ja' // Japanese
  | 'vi' // Vietnamese
  | 'ru' // Russian
  | 'tr' // Turkish
  | 'fr' // French
  | 'es' // Spanish
  | 'ar' // Arabic
  | 'de' // German
  | 'id' // Indonesian
  | 'ms' // Malay
  | 'th' // Thai
  | 'pt' // Portuguese
  | 'it' // Italian
  | 'hi'; // Hindi